import styled, { createGlobalStyle, css } from 'styled-components';
import { lightTheme } from 'themes/light';
import { forDarkTheme, forLightTheme, themeable } from 'themes/utils';
import { Link } from 'ui/Link';
import { animationDuration, sidebarWidth } from 'tokens';
import { CalendarBaseContainer } from 'components/Calendar/Calendar.styled';
import {
    ChevronContainer,
    Text,
    Container as MenuItemContainer,
    SubItemsContainer
} from 'ui/Menu/Menuitem/MenuItem.styled';
import { MenuInnerContainer } from 'ui/Menu/Menu.styled';
import {
    notDesktopMediaQuery,
    desktopMediaQuery,
    NotDesktopContainer,
    OnlyDesktopContainer,
    notMobileMediaQuery
} from 'utils/mediaQuery';

export const BrandContainer = styled.div`
    padding: 24px;
    border-bottom: 1px solid ${({ theme }) => theme.sidebar.lineColor};
    display: flex;
    flex-flow: row;
    justify-content: space-between;
    position: relative;
    transition: border-color ${animationDuration};
`;

export const LogoContainer = styled.div`
    width: auto;
    overflow: hidden;
`;

export const MainContainer = styled.div`
    overflow-y: auto;
    flex: 1;
    overflow-x: hidden;
    display: flex;
    flex-flow: column;
    box-sizing: content-box;

    /* Скрываем скроллбар для Chrome, Safari и Opera */
    &::-webkit-scrollbar {
        width: 3px;
    }

    scrollbar-width: 3px;
`;

export const MenuContainer = styled.nav`
    flex: 1;
    padding: 24px;
`;

export const MenuLabel = styled.div`
    color: ${({ theme }) => theme.sidebar.menuLabelColor};
    font-size: 10px;
    font-weight: 500;
    line-height: 12px;
    letter-spacing: 0.4px;
    margin: 0 12px 8px;
    text-transform: uppercase;
`;

export const ThemeChoiceContainer = styled.div`
    padding: 28px 20px;
    border-top: 1px solid ${({ theme }) => theme.sidebar.lineColor};

    transition: border-color ${animationDuration};
`;

export const ThemeChoicesRow = styled.div`
    display: flex;
    flex-flow: row;
    margin: 0 auto;

    ${forDarkTheme(css`
        background: #161a23;
    `)}

    ${forLightTheme(css`
        border: 1px solid ${lightTheme.sidebar.lineColor};
    `)}

    width: max-content;

    border-radius: 6px;
`;

export const ThemeChoice = styled.button<SActiveProps>`
    flex: 1;
    border-radius: 6px;
    padding: 8px 14px;
    display: flex;
    flex-flow: row;
    gap: 8px;
    color: ${({ theme }) => theme.sidebar.lineColor};
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    letter-spacing: -0.02em;
    background: none;
    outline: none;
    appearance: none;
    border: none;
    cursor: pointer;
    transition-duration: ${animationDuration};
    transition-property: all;

    ${({ $active, theme }) =>
        $active &&
        (theme.name === 'dark'
            ? css`
                  background: #2d303e;
                  border: 1px solid ${theme.sidebar.lineColor};
              `
            : css`
                  border-right: 1px solid ${theme.sidebar.lineColor};
                  background: ${theme.sidebar.menuItemBackground};
              `)}

    & svg * {
        transition: fill 0.2s;
        fill: ${({ theme }) => theme.sidebar.lineColor};
    }
`;

export const ThemeIcon = styled.div`
    max-width: 16px;
    max-height: 16px;
    width: auto;
    height: auto;
`;

export const ThemeToggleIcon = styled(ThemeIcon)<{ $active: boolean }>`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 16px;
    height: 16px;

    transition: all ${animationDuration};

    ${({ $active }) =>
        !$active &&
        css`
            opacity: 0;
        `};
`;

export const ThemeTitle = styled.div``;

export const MainLink = styled(Link)`
    display: flex;
    flex-flow: column;
    justify-content: center;
    width: 100%;
`;

export const CalendarContainer = styled(CalendarBaseContainer)`
    ${notMobileMediaQuery(css`
        top: 0;
        left: 100%;
        margin-left: 54px;
    `)}
`;

export const OrganizationSelectWrapper = styled.div`
    padding: 24px;

    ${notDesktopMediaQuery(css`
        padding-top: 0;
        padding-bottom: 0;
    `)}
`;

export const RoleLabel = styled.div`
    font-size: 8px;
    line-height: 12px;
    font-weight: 500;
    letter-spacing: 0.4px;
    color: ${themeable('sidebar.subItemColor')};
    text-transform: uppercase;
`;

export const ExpanderButton = styled(OnlyDesktopContainer)`
    position: absolute;
    right: -11px;
    transform: translateY(-50%);
    cursor: pointer;
    top: 100%;

    ${forLightTheme(css`
        & svg * {
            transition: all ${animationDuration};
        }

        & rect {
            fill: ${themeable('sidebar.background')};
            stroke: ${themeable('sidebar.lineColor')};
        }

        & path {
            fill: ${themeable('sidebar.lineColor')};
        }
    `)}
`;

export const SidebarWidthStyle = createGlobalStyle<{ $expanded: boolean }>`
    :root {
        ${sidebarWidth.name}: ${({ $expanded }) => ($expanded ? 360 : 90)}px;
    }
`;

export const Sidebar = styled.aside<{ $expanded: boolean } & SOpenProps>`
    display: flex;
    flex-flow: column;
    background: ${({ theme }) => theme.sidebar.background};
    min-height: 100%;
    position: relative;
    transition-property: background-color, transform;
    transition-duration: ${animationDuration};

    ${desktopMediaQuery(css`
        border-radius: 16px;
    `)}

    ${notDesktopMediaQuery(css`
        width: 100%;
        max-width: 275px;
        position: fixed;
        z-index: 52;
        height: 100%;

        background: rgba(31, 29, 43, 0.5);
        backdrop-filter: blur(25px);
    `)}

    ${({ $expanded }) =>
        desktopMediaQuery(css`
            width: ${sidebarWidth};

            ${!$expanded &&
            css`
                &
                    ${ThemeTitle},
                    &
                    ${Text},
                    &
                    ${OrganizationSelectWrapper},
                    &
                    ${ChevronContainer} {
                    display: none;
                }

                & ${MainLink} {
                    align-items: center;
                }

                & ${LogoContainer} {
                    width: 16px;
                }

                & ${RoleLabel} {
                    font-size: 6px;
                }

                & ${BrandContainer}, & ${MenuContainer} {
                    padding-left: 0;
                    padding-right: 0;
                }

                & ${MenuLabel} {
                    margin-left: auto;
                    margin-right: auto;
                    width: max-content;
                    font-size: 14px;
                }

                & ${MenuContainer} {
                    width: 90px;
                }

                & ${MenuInnerContainer} {
                    padding-left: 24px;
                    padding-right: 24px;
                }

                & ${MenuItemContainer}[data-active="false"]:not(:hover) {
                    background: none;
                }

                & ${SubItemsContainer} {
                    position: fixed;
                    margin-left: 73px;
                    margin-top: -41px;
                    z-index: 1000;
                    padding: 8px;
                    border-radius: 12px;
                    flex-flow: row;

                    &::before {
                        content: ' ';
                        position: absolute;
                        top: 12px;
                        left: -7px;
                        width: 0;
                        height: 0;
                        border-top: 8px solid transparent;
                        border-bottom: 8px solid transparent;
                        border-right: 7px solid
                            ${themeable('sidebar.background')};
                    }
                }
            `}
        `)}

    ${({ $isOpen }) =>
        notDesktopMediaQuery(css`
            transform: translateX(${$isOpen ? 0 : -100}%);
        `)}
`;

export const ThemeChoiceShortContainer = styled.div`
    border-radius: 16px;
    width: 52px;
    cursor: pointer;
    padding: 2px;
    background: #161a23;
`;

export const ThemeChangeButton = styled.button<{ $position: 'left' | 'right' }>`
    border-radius: 100%;
    background: ${themeable('secondaryBackground')};
    width: 28px;
    height: 28px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: inherit;
    box-shadow:
        0px 3px 1px 0px rgba(0, 0, 0, 0.06),
        0px 3px 8px 0px rgba(0, 0, 0, 0.15);
    position: relative;
    transition: all ${animationDuration};

    ${({ $position }) =>
        $position === 'left'
            ? css`
                  margin-right: 20px;
              `
            : css`
                  margin-left: 20px;
              `}
`;

export const SidebarOverlay = styled(NotDesktopContainer)`
    position: fixed;
    z-index: 51;
    height: 100%;
    width: 100%;
`;
